import React from 'react';
import i18n from '@/lib/i18n';
import { useTranslation } from 'react-i18next';
import { AliveLogo } from '../brand/AliveLogo';

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * Top-level error boundary. The fallback uses the ALIVE wordmark as
 * the visual centerpiece (not a scary red error icon) — when a user
 * hits an error we want them to feel held by the brand, not yelled
 * at. The "dying" status drives the logo's accent dot to a soft
 * orange pulse, communicating "something is unwell" without alarming.
 */
export class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    this.props.onError?.(error, errorInfo);
  }

  handleReset = (): void => {
    this.setState({ hasError: false, error: null });
  };

  handleReload = (): void => {
    window.location.reload();
  };

  render(): React.ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Class components can't use the `useTranslation` hook, so we
      // call i18next imperatively. The default-language fallback is
      // already wired up in `lib/i18n.ts` (`fallbackLng: 'en'`), so
      // adding strings to en.json is enough to cover all 13 locales.
      const t = i18n.t.bind(i18n);

      return (
        <div className="min-h-dvh flex flex-col items-center justify-center px-6 bg-white dark:bg-[#0B0B0D] text-gray-900 dark:text-gray-100">
          {/* Brand mark — the dying status ties the visual to the
              error context without resorting to a red alert icon. */}
          <div className="mb-10 text-gray-900 dark:text-gray-100">
            <AliveLogo size="xl" status="dying" />
          </div>

          <h1 className="text-lg font-semibold mb-2">
            {t('errorBoundary.title')}
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 text-center mb-8 max-w-xs leading-relaxed">
            {t('errorBoundary.description')}
          </p>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={this.handleReset}
              className="px-4 py-1.5 rounded-full text-xs font-medium border border-gray-200 dark:border-white/[0.12] text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-white/[0.04] transition-colors"
            >
              {t('errorBoundary.retry')}
            </button>
            <button
              type="button"
              onClick={this.handleReload}
              className="px-4 py-1.5 rounded-full text-xs font-medium bg-primary text-white hover:bg-primary/90 transition-colors"
            >
              {t('errorBoundary.reload')}
            </button>
          </div>

          {process.env.NODE_ENV === 'development' && this.state.error && (
            <details className="mt-10 max-w-md w-full">
              <summary className="text-[11px] text-gray-400 dark:text-gray-500 cursor-pointer hover:text-gray-600 dark:hover:text-gray-300 transition-colors">
                {t('errorBoundary.details')}
              </summary>
              <pre className="mt-2 px-3 py-2 rounded bg-gray-50 dark:bg-white/[0.03] text-[10px] font-mono text-gray-600 dark:text-gray-400 overflow-auto max-h-48 leading-relaxed">
                {this.state.error.message}
                {'\n'}
                {this.state.error.stack}
              </pre>
            </details>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Page-level error boundary. Lighter footprint than the top-level
 * one — fits inside an existing page chrome (header, tab bar). Same
 * brand-first visual language: ALIVE logo, no red icons.
 */
export function PageErrorBoundary({ children }: { children: React.ReactNode }) {
  return <ErrorBoundary fallback={<PageErrorFallback />}>{children}</ErrorBoundary>;
}

function PageErrorFallback() {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center justify-center py-20 px-6 text-gray-900 dark:text-gray-100">
      <div className="mb-6 text-gray-900 dark:text-gray-100">
        <AliveLogo size="lg" status="dying" />
      </div>
      <h2 className="text-sm font-semibold mb-1">{t('errorBoundary.pageTitle')}</h2>
      <p className="text-xs text-gray-500 dark:text-gray-400 text-center mb-5 max-w-xs">
        {t('errorBoundary.pageDescription')}
      </p>
      <button
        type="button"
        onClick={() => window.location.reload()}
        className="px-4 py-1.5 rounded-full text-xs font-medium bg-primary text-white hover:bg-primary/90 transition-colors"
      >
        {t('errorBoundary.reload')}
      </button>
    </div>
  );
}

/**
 * Component-level error boundary. The smallest fallback — a single
 * line of secondary text inside a soft container. No icon at all;
 * the failure should be quiet so it doesn't dominate a working page.
 *
 * `name` is the human-readable label for the failed component, e.g.
 * "Feed" / "Chat panel". It's interpolated into the i18n string so
 * each language can render the wrapping copy correctly.
 */
export function ComponentErrorBoundary({
  children,
  name,
}: {
  children: React.ReactNode;
  name?: string;
}) {
  return (
    <ErrorBoundary fallback={<ComponentErrorFallback name={name} />}>
      {children}
    </ErrorBoundary>
  );
}

function ComponentErrorFallback({ name }: { name?: string }) {
  const { t } = useTranslation();
  const label = name ?? t('errorBoundary.componentDefault');
  return (
    <div className="flex items-center justify-center py-6 px-4 text-xs text-gray-400 dark:text-gray-500">
      {t('errorBoundary.componentFailed', { name: label })}
    </div>
  );
}
